import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    DeskField,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
    TINY_BUTTON_CLASS,
} from './AskFrankDesk';
import {
    ENTITY_KINDS,
    ENTITY_SUBTYPES,
    ENTITY_STATUSES,
    EMPTY_ENTITY,
    entityPayload,
} from './knowledgeShared';
import {
    saveAskFrankEntity,
    deleteAskFrankEntity,
    uploadAskFrankEntityImage,
    listAskFrankEntityImages,
} from '../../../services/api';

function RowList({ title, hint, rows, onChange, columns, addLabel }) {
    const update = (index, key, value) => {
        onChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    };

    return (
        <div className="sm:col-span-2">
            <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-700">{title}</span>
                {hint && <span className="text-[11px] italic text-gray-400">{hint}</span>}
            </div>

            {rows.length === 0 && <p className="mb-2 text-[12px] text-gray-500">None yet.</p>}

            {rows.map((row, index) => (
                <div key={index} className="mb-2 flex flex-wrap items-center gap-2">
                    {columns.map(column => (
                        <input
                            key={column.key}
                            type="text"
                            value={row[column.key] || ''}
                            placeholder={column.placeholder}
                            onChange={(e) => update(index, column.key, e.target.value)}
                            className={`min-w-0 rounded border border-gray-300 px-2 py-1 text-[13px] ${column.wide ? 'flex-[2]' : 'flex-1'}`}
                        />
                    ))}
                    <button
                        type="button"
                        onClick={() => onChange(rows.filter((_, i) => i !== index))}
                        className="shrink-0 bg-red-700 px-2 py-1 text-[10px] uppercase text-white"
                    >
                        Remove
                    </button>
                </div>
            ))}

            <button
                type="button"
                onClick={() => onChange([...rows, Object.fromEntries(columns.map(c => [c.key, '']))])}
                className={TINY_BUTTON_CLASS}
            >
                {addLabel}
            </button>
        </div>
    );
}

function EntityEditor({ entityId, initial, onSaved, onCancel, onDeleted }) {
    const [form, setForm] = useState(initial || EMPTY_ENTITY);
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showExtras, setShowExtras] = useState(false);

    useEffect(() => { setForm(initial || EMPTY_ENTITY); }, [initial]);

    useEffect(() => {
        listAskFrankEntityImages()
            .then(res => { if (res.success) setImages(res.images || []); })
            .catch(() => {});
    }, []);

    const uploadImage = async (file) => {
        setUploading(true);
        try {
            const res = await uploadAskFrankEntityImage(file);

            if (!res.success) {
                toast.error(res.message || 'That image would not upload');
                return;
            }

            setForm(current => ({ ...current, imageUrl: res.url }));

            const listed = await listAskFrankEntityImages();
            if (listed.success) setImages(listed.images || []);
        } finally {
            setUploading(false);
        }
    };

    const submit = async (event) => {
        event.preventDefault();

        if (!form.name.trim()) {
            toast.error('Give the entry a name');
            return;
        }

        setBusy(true);
        const res = await saveAskFrankEntity(entityId, entityPayload(form));
        setBusy(false);

        if (res.success) {
            toast.success('Entry saved');
            onSaved(res.entity);
        } else {
            toast.error(res.message || 'That did not save');
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${form.name}?`)) return;

        setBusy(true);
        const res = await deleteAskFrankEntity(entityId);
        setBusy(false);

        if (res.success) {
            toast.success('Entry removed');
            onDeleted();
        } else {
            toast.error(res.message || 'That did not work');
        }
    };

    const kindHint = ENTITY_KINDS.find(kind => kind.value === form.kind)?.hint;
    const statusHint = ENTITY_STATUSES.find(status => status.value === form.status)?.hint;

    return (
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DeskField label="Name" required>
                <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ozzy Osbourne"
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Kind" hint={kindHint}>
                <select
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className={DESK_INPUT_CLASS}
                >
                    {ENTITY_KINDS.map(kind => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
                </select>
            </DeskField>

            <DeskField label="Status" hint={statusHint}>
                <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className={DESK_INPUT_CLASS}
                >
                    {ENTITY_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                </select>
            </DeskField>

            <DeskField label="Subtype" hint="optional, narrows the kind">
                <select
                    value={form.subtype}
                    onChange={(e) => setForm({ ...form, subtype: e.target.value })}
                    className={DESK_INPUT_CLASS}
                >
                    {ENTITY_SUBTYPES.map(subtype => (
                        <option key={subtype || 'none'} value={subtype}>{subtype || 'none'}</option>
                    ))}
                </select>
            </DeskField>

            <DeskField label="Headline" hint="the one line under the name" className="sm:col-span-2">
                <input
                    type="text"
                    value={form.headline}
                    onChange={(e) => setForm({ ...form, headline: e.target.value })}
                    placeholder="The Prince of Darkness, and briefly a Habbo"
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Summary" hint="what Frank says out loud" className="sm:col-span-2">
                <textarea
                    rows={3}
                    value={form.summary}
                    onChange={(e) => setForm({ ...form, summary: e.target.value })}
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <RowList
                title="Linked terms"
                hint="other names and spellings that should find this entry"
                rows={form.aliases}
                onChange={(aliases) => setForm({ ...form, aliases })}
                columns={[{ key: 'alias', placeholder: 'Ozzy', wide: true }]}
                addLabel="Add a linked term"
            />

            <DeskField
                label="Context cues"
                hint="separate with commas"
                className="sm:col-span-2"
            >
                <input
                    type="text"
                    value={form.cues}
                    onChange={(e) => setForm({ ...form, cues: e.target.value })}
                    placeholder="worth, value, rare, catalogue"
                    className={DESK_INPUT_CLASS}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                    When a name means several things, these are the words that mean <em>this</em> one. A question
                    carrying any of them picks this sense over the others sharing the name.
                </p>
            </DeskField>

            <div className="sm:col-span-2">
                <button type="button" onClick={() => setShowExtras(v => !v)} className={DESK_BUTTON_CLASS}>
                    {showExtras ? 'Hide the full card' : 'The full card'}
                </button>
                <span className="ml-2 text-[11px] text-gray-400">
                    picture, long read, facts, scrapbook, link and ranking
                </span>
            </div>

            {showExtras && (
                <>
                    <DeskField label="Body" hint="the longer read on the full card" className="sm:col-span-2">
                        <textarea
                            rows={5}
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            className={DESK_INPUT_CLASS}
                        />
                    </DeskField>

                    <DeskField label="Portrait image" hint="upload below, or paste a URL">
                        <input
                            type="text"
                            value={form.imageUrl}
                            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                            className={DESK_INPUT_CLASS}
                        />

                        <div className="mt-2 flex items-start gap-3">
                            {form.imageUrl && (
                                <img src={form.imageUrl} alt="" className="h-[74px] w-auto border border-gray-300 bg-offwhite" />
                            )}

                            <div className="flex flex-col gap-1">
                                <input
                                    type="file"
                                    accept="image/png,image/gif,image/webp,image/jpeg"
                                    disabled={uploading}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (file) uploadImage(file);
                                    }}
                                    className="text-[11px] text-gray-700"
                                />
                                <span className="text-[10px] text-gray-500">
                                    {uploading ? 'Uploading...' : 'PNG, GIF, WebP or JPEG, up to 2MB'}
                                </span>
                                {form.imageUrl && (
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, imageUrl: '' })}
                                        className={`self-start ${TINY_BUTTON_CLASS}`}
                                    >
                                        Clear image
                                    </button>
                                )}
                            </div>
                        </div>

                        {images.length > 0 && (
                            <div className="mt-2">
                                <span className="text-[10px] text-gray-500">Already uploaded</span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {images.map(image => (
                                        <button
                                            key={image.filename}
                                            type="button"
                                            title={image.filename}
                                            onClick={() => setForm({ ...form, imageUrl: image.url })}
                                            className={`border bg-offwhite p-[2px] ${form.imageUrl === image.url ? 'border-maroon' : 'border-gray-300'}`}
                                        >
                                            <img src={image.url} alt="" className="h-[42px] w-auto" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </DeskField>

                    <div className="flex flex-col gap-3">
                        <DeskField label="Link URL" hint="where the card's button goes">
                            <input
                                type="text"
                                value={form.linkUrl}
                                onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
                                placeholder="https://... or /profile/Steve"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Link label">
                            <input
                                type="text"
                                value={form.linkLabel}
                                onChange={(e) => setForm({ ...form, linkLabel: e.target.value })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Priority" hint="ties break to the higher number">
                            <input
                                type="number"
                                step="0.1"
                                value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>
                    </div>

                    <label className="flex items-start gap-2 text-[12px] text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.autoFacts}
                            onChange={(e) => setForm({ ...form, autoFacts: e.target.checked })}
                        />
                        <span>Let Frank add facts of his own, from the profile, the value ledger and the index</span>
                    </label>

                    <label className="flex items-start gap-2 text-[12px] text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.protected}
                            onChange={(e) => setForm({ ...form, protected: e.target.checked })}
                        />
                        <span>Curated only, never show anything Frank worked out himself</span>
                    </label>

                    <RowList
                        title="Facts on the card"
                        rows={form.facts}
                        onChange={(facts) => setForm({ ...form, facts })}
                        columns={[
                            { key: 'label', placeholder: 'Visited Habbo' },
                            { key: 'value', placeholder: 'March 2007, in the Rock Lobby', wide: true },
                            { key: 'url', placeholder: 'Link (optional)', wide: true },
                        ]}
                        addLabel="Add a fact"
                    />

                    <RowList
                        title="Scrapbook images"
                        rows={form.media}
                        onChange={(media) => setForm({ ...form, media })}
                        columns={[
                            { key: 'imageUrl', placeholder: 'https://...', wide: true },
                            { key: 'caption', placeholder: 'Caption', wide: true },
                            { key: 'credit', placeholder: 'Credit' },
                        ]}
                        addLabel="Add an image"
                    />
                </>
            )}

            <div className="flex flex-wrap gap-2 sm:col-span-2">
                <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    {entityId ? 'Save entry' : 'Create entry'}
                </button>

                <button type="button" onClick={onCancel} className={DESK_BUTTON_CLASS}>
                    Cancel
                </button>

                {entityId && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={remove}
                        className="ml-auto bg-red-700 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white disabled:opacity-50"
                    >
                        Delete entry
                    </button>
                )}
            </div>
        </form>
    );
}

export default EntityEditor;
